import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon, ShoppingBagIcon, WrenchIcon, BuildingIcon, BookOpenIcon, HelpCircleIcon, ShieldCheckIcon, ArrowRightIcon, MapPinIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface NavbarProps {
  onNavigate: (page: string) => void;
  currentPage: string;
}
interface NavChild {
  name: string;
  page: string;
  icon: React.ReactNode;
  description: string;
}
interface NavItem {
  name: string;
  page?: string;
  children?: NavChild[];
}
export function Navbar({
  onNavigate,
  currentPage
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navItems: NavItem[] = [{
    name: 'Home',
    page: 'home'
  }, {
    name: 'About Us',
    page: 'about'
  }, {
    name: 'Rentals',
    children: [{
      name: 'Products',
      page: 'products',
      icon: <ShoppingBagIcon className="w-5 h-5" data-id="element-233" />,
      description: 'Browse our mobility equipment'
    }, {
      name: 'Services',
      page: 'services',
      icon: <WrenchIcon className="w-5 h-5" data-id="element-234" />,
      description: 'What we offer our customers'
    }, {
      name: 'Hotel Rentals',
      page: 'hotel-rentals',
      icon: <BuildingIcon className="w-5 h-5" data-id="element-235" />,
      description: 'Equipment delivered to your hotel'
    }, {
      name: 'Hotspot',
      page: 'hotspot',
      icon: <MapPinIcon className="w-5 h-5" data-id="element-236" />,
      description: 'See all our delivery locations'
    }]
  }, {
    name: 'Resources',
    children: [{
      name: 'Blogs',
      page: 'blogs',
      icon: <BookOpenIcon className="w-5 h-5" data-id="element-237" />,
      description: 'Latest news and helpful tips'
    }, {
      name: 'FAQs',
      page: 'faq',
      icon: <HelpCircleIcon className="w-5 h-5" data-id="element-238" />,
      description: 'Answers to common questions'
    }, {
      name: 'Cancellation Policy',
      page: 'cancellation-policy',
      icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-239" />,
      description: 'Our cancellation terms'
    }]
  }, {
    name: 'Agents/Partners',
    page: 'partners'
  }, {
    name: 'Contact Us',
    page: 'contact'
  }];
  const isSolid = isScrolled || currentPage !== 'home';
  const handleNavClick = (page: string) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    setOpenMobileDropdown(null);
  };
  const handleDropdownEnter = (name: string) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setOpenDropdown(name);
  };
  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };
  const isChildActive = (item: NavItem) => {
    return item.children?.some(child => child.page === currentPage) ?? false;
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isSolid ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'}`} data-id="element-240">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-241">
        <div className="flex justify-between items-center" data-id="element-242">
          {/* Logo */}
          <button onClick={() => handleNavClick('home')} className="flex items-center gap-2 z-50 cursor-pointer" data-id="element-243">
            <div className={`bg-white rounded-xl px-4 py-2 shadow-md transition-all duration-300 ${!isSolid ? 'shadow-lg' : 'shadow-sm'}`} data-id="element-244">
              <img src="https://cdn.magicpatterns.com/uploads/8EY7ZJX3JwCeaKXUAMAeGe/logo-rfwu7jdkpjy48aomhgtuqqnjwynn4g1lfd5t8lacs0.png" alt="WheelAround Rentals Logo" className="h-10 md:h-12 w-auto object-contain" data-id="element-245" />
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-7" data-id="element-246">
            {navItems.map(item => {
            if (item.children) {
              const isActive = isChildActive(item);
              return <div key={item.name} className="relative" onMouseEnter={() => handleDropdownEnter(item.name)} onMouseLeave={handleDropdownLeave} data-id="element-247">
                    <button className={`text-sm font-medium transition-colors hover:text-brand-red cursor-pointer flex items-center gap-1 ${isActive ? 'text-brand-red' : isSolid ? 'text-brand-charcoal' : 'text-white drop-shadow-md'}`} data-id="element-248">
                      {item.name}
                      <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} data-id="element-249" />
                    </button>

                    <AnimatePresence data-id="element-250">
                      {openDropdown === item.name && <motion.div initial={{
                    opacity: 0,
                    y: 8,
                    scale: 0.97
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: 8,
                    scale: 0.97
                  }} transition={{
                    duration: 0.18,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="absolute top-full left-0 pt-4" data-id="element-251">
                          {/* Arrow pointer */}
                          <div className="absolute top-[10px] left-6 w-3 h-3 bg-white rotate-45 border-l border-t border-gray-100 z-10" data-id="element-252" />

                          <div className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-gray-100/80 min-w-[280px] overflow-hidden relative" data-id="element-253">
                            {/* Header */}
                            <div className="px-5 pt-4 pb-2" data-id="element-254">
                              <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-charcoal/40" data-id="element-255">
                                {item.name}
                              </p>
                            </div>

                            {/* Items */}
                            <div className="px-2 pb-3 space-y-0.5" data-id="element-256">
                              {item.children.map(child => {
                          const isActive = currentPage === child.page;
                          return <button key={child.page} onClick={() => handleNavClick(child.page)} className={`w-full text-left px-3 py-3 rounded-xl flex items-start gap-3 transition-all duration-150 cursor-pointer group ${isActive ? 'bg-brand-red/5' : 'hover:bg-gray-50'}`} data-id="element-257">
                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isActive ? 'bg-brand-red/10 text-brand-red' : 'bg-gray-100 text-brand-charcoal/50 group-hover:bg-brand-red/10 group-hover:text-brand-red'}`} data-id="element-258">
                                      {child.icon}
                                    </div>
                                    <div className="flex-1 min-w-0" data-id="element-259">
                                      <div className="flex items-center gap-1.5" data-id="element-260">
                                        <span className={`text-sm font-semibold transition-colors ${isActive ? 'text-brand-red' : 'text-brand-navy group-hover:text-brand-red'}`} data-id="element-261">
                                          {child.name}
                                        </span>
                                        <ArrowRightIcon className={`w-3 h-3 transition-all ${isActive ? 'text-brand-red opacity-100' : 'text-brand-charcoal/30 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-brand-red'}`} data-id="element-262" />
                                      </div>
                                      <p className="text-xs text-brand-charcoal/45 mt-0.5 leading-snug" data-id="element-263">
                                        {child.description}
                                      </p>
                                    </div>
                                  </button>;
                        })}
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            }
            return <button key={item.name} onClick={() => handleNavClick(item.page!)} className={`text-sm font-medium transition-colors hover:text-brand-red cursor-pointer ${currentPage === item.page ? 'text-brand-red' : isSolid ? 'text-brand-charcoal' : 'text-white drop-shadow-md'}`} data-id="element-264">
                  {item.name}
                </button>;
          })}
            <button onClick={() => handleNavClick('reserve')} className="bg-brand-red text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors shadow-lg cursor-pointer" data-id="element-265">
              Reserve Now
            </button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden z-50 p-2 cursor-pointer" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-266">
            {isMobileMenuOpen ? <XIcon className={`h-6 w-6 ${isSolid || isMobileMenuOpen ? 'text-brand-navy' : 'text-white'}`} data-id="element-267" /> : <MenuIcon className={`h-6 w-6 ${isSolid ? 'text-brand-navy' : 'text-white'}`} data-id="element-268" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Panel */}
      <AnimatePresence data-id="element-269">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="absolute top-0 left-0 right-0 bg-white shadow-xl pt-20 pb-6 px-4 lg:hidden" data-id="element-270">
            <nav className="flex flex-col space-y-1" data-id="element-271">
              {navItems.map(item => {
            if (item.children) {
              const isOpen = openMobileDropdown === item.name;
              const isActive = isChildActive(item);
              return <div key={item.name} data-id="element-272">
                      <button onClick={() => setOpenMobileDropdown(isOpen ? null : item.name)} className={`w-full flex items-center justify-between font-medium text-lg py-3 border-b border-gray-100 text-left cursor-pointer ${isActive ? 'text-brand-red' : 'text-brand-navy'}`} data-id="element-273">
                        {item.name}
                        <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-274" />
                      </button>
                      <AnimatePresence data-id="element-275">
                        {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-276">
                            <div className="pl-2 py-2 space-y-1" data-id="element-277">
                              {item.children.map(child => {
                        const isActive = currentPage === child.page;
                        return <button key={child.page} onClick={() => handleNavClick(child.page)} className={`w-full text-left py-2.5 px-3 rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${isActive ? 'text-brand-red bg-brand-red/5' : 'text-brand-charcoal/70 hover:text-brand-red'}`} data-id="element-278">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isActive ? 'bg-brand-red/10 text-brand-red' : 'bg-gray-100 text-brand-charcoal/40'}`} data-id="element-279">
                                      {child.icon}
                                    </div>
                                    <div data-id="element-280">
                                      <span className="text-base font-medium" data-id="element-281">
                                        {child.name}
                                      </span>
                                      <p className="text-xs text-brand-charcoal/40 mt-0.5" data-id="element-282">
                                        {child.description}
                                      </p>
                                    </div>
                                  </button>;
                      })}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
            }
            return <button key={item.name} onClick={() => handleNavClick(item.page!)} className={`font-medium text-lg py-3 border-b border-gray-100 text-left cursor-pointer ${currentPage === item.page ? 'text-brand-red' : 'text-brand-navy'}`} data-id="element-283">
                    {item.name}
                  </button>;
          })}
              <button onClick={() => handleNavClick('reserve')} className="bg-brand-red text-white text-center px-6 py-3 rounded-full font-semibold mt-4 shadow-md cursor-pointer" data-id="element-284">
                Reserve Now
              </button>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}